import { useState } from 'react';

import { toast } from 'sonner';

import { Permission } from '../../../../../../backend/src/auth/permissions/permission';
import { OrgPermission } from '../../../../../../backend/src/organisation/organisation-permission';
import { useUpdateExternalUser2fa } from '@/api/mutations/useUpdateExternalUser2fa';
import { useUpdateRequireMatterCode } from '@/api/mutations/useUpdateRequireMatterCode';
import { useUpdateRequirePracticeArea } from '@/api/mutations/useUpdateRequirePracticeArea';
import { useUserContext } from '@/Contexts/User';
import { useOrgPermissions } from '@/hooks/orgPermissions';
import { PermissionGate } from '@/hooks/useHasPermission';
import { SettingsContent, SettingsRow, SettingsSection } from '@/new/components/settings-section';
import { Switch } from '@/new/components/shadcn/switch';

export function General() {
  const { user } = useUserContext();
  const { isEnabled } = useOrgPermissions();
  const externalUser2fa = useUpdateExternalUser2fa();

  const updateRequireMatterCode = useUpdateRequireMatterCode();
  const updateRequirePracticeArea = useUpdateRequirePracticeArea();

  // Optimistic local state so the switch reflects the toggle immediately.
  const [requireMatterCode, setRequireMatterCode] = useState(user?.organisation?.requireMatterCode ?? false);
  const [requirePracticeArea, setRequirePracticeArea] = useState(user?.organisation?.requirePracticeArea ?? false);

  const handleToggle = async (checked: boolean) => {
    setRequireMatterCode(checked);
    try {
      await updateRequireMatterCode.mutateAsync({ requireMatterCode: checked });
      toast.success(`Matter codes are now ${checked ? 'required' : 'optional'} for new matters`);
    } catch {
      setRequireMatterCode(!checked);
      toast.error('Failed to update matter code setting');
    }
  };

  const handlePracticeAreaToggle = async (checked: boolean) => {
    setRequirePracticeArea(checked);
    try {
      await updateRequirePracticeArea.mutateAsync(checked);
      toast.success(`Practice areas are now ${checked ? 'required' : 'optional'}`);
    } catch {
      setRequirePracticeArea(!checked);
      toast.error('Failed to update practice area setting');
    }
  };

  return (
    <SettingsContent>
      <SettingsSection title="General" description="Configure general settings for your organisation.">
        <PermissionGate permission={Permission.OrganisationManage} uiMode="disabled">
          <SettingsRow
            title="Require Matter code"
            description="When enabled, a Matter code must be provided when creating a new Matter."
          >
            <Switch
              id="require-matter-code"
              checked={requireMatterCode}
              onCheckedChange={handleToggle}
              disabled={updateRequireMatterCode.isLoading}
            />
          </SettingsRow>
        </PermissionGate>
        <PermissionGate permission={Permission.OrganisationManage} uiMode="disabled">
          <SettingsRow
            title="Require practice area for users"
            description="When enabled, users must choose a practice area before they can use Wexler."
          >
            <Switch
              id="require-practice-area"
              checked={requirePracticeArea}
              onCheckedChange={handlePracticeAreaToggle}
              disabled={updateRequirePracticeArea.isLoading}
            />
          </SettingsRow>
        </PermissionGate>
      </SettingsSection>

      <SettingsSection
        title="External user security"
        description="Control sign-in requirements for users who do not use your organisation's SSO."
      >
        <PermissionGate permission={Permission.OrganisationManage} uiMode="disabled">
          <SettingsRow
            title="Require two-factor authentication"
            description="Non-SSO users must enable 2FA before accessing Wexler. For SSO users, your identity provider manages authentication."
          >
            <Switch
              id="external-user-security"
              aria-label="Require two-factor authentication for non-SSO users"
              checked={isEnabled(OrgPermission.Require2FANonSSO)}
              disabled={externalUser2fa.isLoading}
              onCheckedChange={(enabled) =>
                externalUser2fa.mutate(enabled, {
                  onSuccess: () => {
                    toast.success(
                      `Two-factor authentication is now ${enabled ? 'required' : 'optional'} for non-SSO users`,
                    );
                  },
                  onError: (error) => {
                    toast.error(error.message);
                  },
                })
              }
            />
          </SettingsRow>
        </PermissionGate>
      </SettingsSection>
    </SettingsContent>
  );
}
